<template>
  <div>
    <Layout ref="layoutRef" :selectedOption="1" />
    <div class="container">
      <h2>Observaciones de Trámite</h2>
      
      <!-- Formulario para ingresar idTramite y observacionTipo -->
      <div class="input-group">
        <label for="idTramite">Trámite:</label>
        <input
          type="number"
          v-model="localIdTramite"
          placeholder="Ingrese ID del trámite"
          id="idTramite"
        />
        <label for="observacionTipo">Tipo de Observación:</label>
        <select
          v-model="localObservacionTipo"
          id="observacionTipo"
        >
          <option value="" disabled>Seleccione un tipo</option>
          <option value="M">M: Auditoría Médica</option>
          <option value="D">D: Gestión Documental</option>
        </select>
        <button @click="fetchObservaciones">Buscar Observaciones</button>
      </div>

      <!-- Mensaje de error o carga -->
      <p v-if="error" class="error">{{ error }}</p>
      <p v-if="loading" class="loading">Cargando...</p>

      <!-- Tabla para mostrar observaciones con detalles anidados -->
      <div v-if="planillas.length > 0" class="table-container">
        <table>
          <thead>
            <tr>
              <th></th>
              <th>Trámite</th>
              <th>Número</th>
              <th>Nombre Paciente</th>
              <th>Observación Codificada</th>
              <th>Observación Complementaria</th>
              <th>Tipo Observación</th>
              <th>Código Detalle</th>
              <th>Descripción Detalle</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="planilla in planillas" :key="planilla.planillaNumero">
              <!-- Fila de la planilla -->
              <tr class="planilla-row">
                <td>
                  <button
                    v-if="planilla.detalles.some(d => d.observacionTipo === localObservacionTipo)"
                    @click="planilla.expanded = !planilla.expanded"
                  >
                    {{ planilla.expanded ? '−' : '+' }}
                  </button>
                  <span v-else>Sin detalles</span>
                </td>
                <td>{{ planilla.idTramite }}</td>
                <td>{{ planilla.planillaNumero }}</td>
                <td>{{ planilla.planillaNombre }}</td>
                <td>-</td>
                <td>-</td>
                <td>-</td>
                <td>-</td>
                <td>-</td>
              </tr>
              <!-- Observaciones de la planilla -->
              <tr
                v-for="observacion in planilla.observaciones"
                :key="observacion.id"
                class="observacion-row"
              >
                <td></td>
                <td>{{ observacion.idTramite }}</td>
                <td>{{ observacion.planillaNumero }}</td>
                <td>{{ observacion.planillaNombre }}</td>
                <td>{{ observacion.observacionDescripcion }}</td>
                <td>{{ observacion.descripcion }}</td>
                <td>{{ observacion.observacionTipo }}</td>
                <td>-</td>
                <td>-</td>
              </tr>
              <!-- Filas de detalles (mostradas si está expandido, según localObservacionTipo) -->
              <template v-if="planilla.expanded">
                <tr
                  v-for="detalle in planilla.detalles.filter(d => d.observacionTipo === localObservacionTipo)"
                  :key="detalle.id"
                  class="detalle-row"
                >
                  <td></td>
                  <td>{{ detalle.idTramite }}</td>
                  <td>-</td>
                  <td>-</td>
                  <td>{{ detalle.observacionDescripcion }}</td>
                  <td>{{ detalle.descripcion }}</td>
                  <td>{{ detalle.observacionTipo }}</td>
                  <td>{{ detalle.planillaDetCodigo }}</td>
                  <td>{{ detalle.planillaDetDescripcion }}</td>
                </tr>
              </template>
            </template>
          </tbody>
        </table>
        <button @click="downloadCSV">Descargar CSV</button>
      </div>
      <p v-else-if="!loading && !error">No se encontraron planillas para el trámite especificado.</p>
    </div>
    <Footer />
  </div>
</template>

<script>
import api from "@/services/api";
import Papa from 'papaparse';
import Layout from "@/components/Layout.vue";
import Footer from "@/components/Footer.vue";

export default {
  name: 'ObservacionesTramite',
  components: {
    Layout,
    Footer,
  },
  props: {
    idTramite: {
      type: [String, Number],
      required: true,
    },
    observacionTipo: {
      type: String,
      default: 'M',
    },
  },
  data() {
    return {
      localIdTramite: this.idTramite,
      localObservacionTipo: this.observacionTipo,
      planillas: [],
      loading: false,
      error: null,
    };
  },
  watch: {
    idTramite(newValue) {
      this.localIdTramite = newValue;
      this.fetchObservaciones();
    },
    observacionTipo(newValue) {
      this.localObservacionTipo = newValue;
      this.fetchObservaciones();
    },
  },
  async mounted() {
    this.$refs.layoutRef.toggleProcesos();
    this.$refs.layoutRef.toggleReportes();
    this.$refs.layoutRef.toggleParametros();
    this.$refs.layoutRef.toggleAyuda();
    if (this.localIdTramite) {
      await this.fetchObservaciones();
    }
  },
  methods: {
    async fetchObservaciones() {
      if (!this.localIdTramite || !this.localObservacionTipo) {
        this.error = 'Por favor, ingrese ID Trámite y Tipo de Observación';
        return;
      }
      this.loading = true;
      this.error = null;
      this.planillas = [];

      try {
        // Llamada al primer endpoint (A)
        console.log(`Fetching planillas for idTramite: ${this.localIdTramite}, observacionTipo: ${this.localObservacionTipo}`);
        const responseA = await api.get(
          `/vwplaobss/tramite-tipo/${this.localIdTramite}/${this.localObservacionTipo}`
        );
        console.log('Response A:', responseA.data);

        // Agrupar observaciones por planillaNumero
        const planillasMap = new Map();
        responseA.data.forEach(item => {
          const planillaNumero = item.planillaNumero || 'Sin número';
          if (!planillasMap.has(planillaNumero)) {
            planillasMap.set(planillaNumero, {
              id: `planilla-${item.idPlanilla}`,
              idTramite: item.idTramite,
              idPlanilla: item.idPlanilla,
              planillaNumero,
              planillaNombre: item.planillaNombre || 'Sin nombre',
              observaciones: [],
              detalles: [],
              expanded: false,
            });
          }
          planillasMap.get(planillaNumero).observaciones.push({
            id: `observacion-${item.idPlaObs}`,
            idTramite: item.idTramite,
            idPlanilla: item.idPlanilla,
            planillaNumero,
            planillaNombre: item.planillaNombre || 'Sin nombre',
            observacionDescripcion: item.observacionDescripcion || 'Sin descripción',
            descripcion: item.descripcion || 'Sin descripción',
            observacionTipo: item.observacionTipo || 'Sin tipo',
          });
        });

        // Obtener IDs de planillas únicas
        const idPlanillas = [...new Set(responseA.data.map(item => item.idPlanilla))];
        console.log('Unique idPlanillas:', idPlanillas);

        // Llamadas al segundo endpoint (B) para cada idPlanilla
        const detallesPromises = idPlanillas.map(idPlanilla =>
          api.get(`/vwpladetobss/idplanilla/${idPlanilla}`).catch(error => {
            console.warn(`Error fetching details for idPlanilla ${idPlanilla}:`, error.message);
            return { data: [] }; // Devolver un array vacío si falla
          })
        );
        const responsesB = await Promise.all(detallesPromises);

        // Asignar detalles a las planillas correspondientes
        responsesB.forEach(response => {
          const detalles = response.data;
          if (detalles.length > 0) {
            const idPlanilla = detalles[0].idPlanilla;
            planillasMap.forEach(planilla => {
              if (planilla.idPlanilla === idPlanilla) {
                planilla.detalles = detalles.map(item => ({
                  id: `detalle-${item.idPlaDetObs}`,
                  idTramite: item.idTramite,
                  idPlanilla: item.idPlanilla,
                  observacionDescripcion: item.observacionDescripcion || 'Sin descripción',
                  descripcion: item.descripcion || 'Sin descripción',
                  observacionTipo: item.observacionTipo || 'Sin tipo',
                  planillaDetCodigo: item.planillaDetCodigo || 'Sin código',
                  planillaDetDescripcion: item.planillaDetDescripcion || 'Sin descripción',
                }));
              }
            });
          }
        });

        // Convertir el mapa a una lista y ordenar por planillaNumero
        this.planillas = [...planillasMap.values()].sort((a, b) =>
          a.planillaNumero.localeCompare(b.planillaNumero)
        );
        console.log('Final planillas:', this.planillas);
      } catch (error) {
        this.error = 'Error al obtener las observaciones: ' + error.message;
        console.error('Fetch error:', error);
      } finally {
        this.loading = false;
      }
    },
    downloadCSV() {
      const csvData = [];
      this.planillas.forEach(planilla => {
        // Fila de la planilla
        csvData.push({
          'Nivel': 'Planilla',
          'ID Trámite': planilla.idTramite,
          'ID Planilla': planilla.idPlanilla,
          'Número Planilla': planilla.planillaNumero,
          'Nombre Paciente': planilla.planillaNombre,
          'Observación Codificada': '-',
          'Observación Complementaria': '-',
          'Tipo Observación': '-',
          'Código Detalle': '-',
          'Descripción Detalle': '-',
        });
        // Observaciones de la planilla
        planilla.observaciones.forEach(observacion => {
          csvData.push({
            'Nivel': 'Observación',
            'ID Trámite': observacion.idTramite,
            'ID Planilla': observacion.idPlanilla,
            'Número Planilla': observacion.planillaNumero,
            'Nombre Paciente': observacion.planillaNombre,
            'Observación Codificada': observacion.observacionDescripcion,
            'Observación Complementaria': observacion.descripcion,
            'Tipo Observación': observacion.observacionTipo,
            'Código Detalle': '-',
            'Descripción Detalle': '-',
          });
        });
        // Detalles de la planilla (según localObservacionTipo)
        planilla.detalles
          .filter(detalle => detalle.observacionTipo === this.localObservacionTipo)
          .forEach(detalle => {
            csvData.push({
              'Nivel': 'Detalle',
              'ID Trámite': detalle.idTramite,
              'ID Planilla': detalle.idPlanilla,
              'Número Planilla': '-',
              'Nombre Paciente': '-',
              'Observación Codificada': detalle.observacionDescripcion,
              'Observación Complementaria': detalle.descripcion,
              'Tipo Observación': detalle.observacionTipo,
              'Código Detalle': detalle.planillaDetCodigo,
              'Descripción Detalle': detalle.planillaDetDescripcion,
            });
          });
      });

      // Agregar BOM para UTF-8
      const BOM = '\uFEFF';
      const csv = BOM + Papa.unparse(csvData, { delimiter: ';' });
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `observaciones_tramite_${this.localIdTramite}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
  },
};
</script>

<style scoped>
.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}
.input-group {
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
}
.input-group label {
  font-weight: bold;
}
.input-group input,
.input-group select {
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
}
.input-group button {
  padding: 8px 16px;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}
.input-group button:hover {
  background-color: #0056b3;
}
.table-container {
  overflow-x: auto;
}
table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 20px;
}
th, td {
  border: 1px solid #ddd;
  padding: 8px;
  text-align: left;
}
th {
  background-color: #f2f2f2;
  font-weight: bold;
}
.planilla-row {
  background-color: #e6f3ff;
}
.observacion-row {
  background-color: #f0f0f0;
}
.detalle-row {
  background-color: #f9f9f9;
}
.detalle-row td:first-child,
.observacion-row td:first-child {
  padding-left: 20px;
}
.error {
  color: red;
}
.loading {
  color: blue;
}
button {
  padding: 8px 16px;
  background-color: #28a745;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}
button:hover {
  background-color: #218838;
}
</style>